/**
 * E2E Manager V2 — orchestrates key pair lifecycle and message decryption.
 *
 * Uses libsodium sealed boxes for key wrapping and
 * XChaCha20-Poly1305 for message encryption.
 */
import {
  ensureSodiumReady,
  generateKeyPair,
  decryptConversationKeyV2,
  decryptMessageV2,
} from "./crypto_service"
import * as KeyStore from "./key_store"

/**
 * Initialize sodium WASM. Must be called once before any E2E operation.
 */
export async function initialize() {
  await ensureSodiumReady()
}

/**
 * Set the current user for key store scoping.
 * Must be called before ensureKeyPairExists/getConversationKey.
 *
 * @param {number|string} userId
 */
export function setCurrentUser(userId) {
  KeyStore.setCurrentUser(userId)
}

/**
 * Ensure the web device has a key pair.
 * If none exists in IndexedDB, generates one and uploads the
 * public key to the server via POST /web/encryption_keys.
 *
 * @returns {Promise<boolean>} true if key pair is ready
 */
export async function ensureKeyPairExists() {
  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    // If we have a local key pair, verify the server still knows it.
    // After a DB reset or migration the server may have lost our public key.
    if (await KeyStore.hasKeyPair()) {
      const localPub = await KeyStore.getPublicKey()
      const checkRes = await fetch("/web/encryption_keys/check")
      if (checkRes.ok) {
        const checkJson = await checkRes.json()
        if (checkJson.public_key === localPub) return true
      }
      // Server doesn't have our key — re-upload the existing one
      const res = await fetch("/web/encryption_keys", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          public_key: localPub,
          key_version: 2,
        }),
      })
      if (res.ok) return true
      // If re-upload fails, fall through to generate a new key pair
    }

    const kp = generateKeyPair()

    const res = await fetch("/web/encryption_keys", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({
        public_key: kp.publicKey,
        key_version: 2,
      }),
    })

    if (!res.ok) {
      console.warn("[E2E] Failed to upload web public key:", res.status)
      return false
    }

    await KeyStore.saveKeyPair(kp.publicKey, kp.privateKey)
    return true
  } catch (err) {
    console.warn("[E2E] ensureKeyPairExists failed:", err)
    return false
  }
}

/**
 * Get the conversation symmetric key, fetching from server if needed.
 *
 * @param {number} conversationId
 * @returns {Promise<Uint8Array|null>} 32-byte key or null
 */
export async function getConversationKey(conversationId) {
  try {
    // 1. Check local cache
    const cached = await KeyStore.getConversationKey(conversationId)
    if (cached) return cached instanceof Uint8Array ? cached : new Uint8Array(cached)

    // 2. Fetch from server
    const res = await fetch(`/web/conversations/${conversationId}/encryption_key`)
    if (!res.ok) return null

    const json = await res.json()
    if (!json.data) return null

    const { sealed_key, key_version } = json.data
    if (!sealed_key) return null

    // 3. Unseal using our key pair
    const publicKey = await KeyStore.getPublicKey()
    const privateKey = await KeyStore.getPrivateKey()
    if (!publicKey || !privateKey) return null

    const convKey = decryptConversationKeyV2(sealed_key, publicKey, privateKey)

    // 4. Cache locally
    await KeyStore.saveConversationKey(conversationId, convKey)
    return convKey
  } catch (err) {
    console.warn("[E2E] getConversationKey failed:", err)
    return null
  }
}

/**
 * Decrypt an encrypted message body (V2 — no separate MAC).
 *
 * @param {string}    encryptedBody - base64 ciphertext
 * @param {string}    nonce         - base64 nonce
 * @param {Uint8Array} convKey      - conversation symmetric key
 * @returns {Promise<string|null>}   plaintext or null on failure
 */
export async function decryptMessageBody(encryptedBody, nonce, convKey) {
  try {
    return decryptMessageV2(encryptedBody, nonce, convKey)
  } catch (err) {
    console.warn("[E2E] decryptMessageBody failed:", err)
    return null
  }
}
